import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Download, Loader2, Signal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CAUSE_META, CauseBadge, humanSecs } from "./offlineCauses";

const PERIODS = [7, 30, 90];
const pctCls = (p) => (p >= 99 ? "text-emerald-600" : p >= 95 ? "text-amber-600" : "text-rose-600");

const CauseTotals = ({ totals }) => {
  const list = Object.entries(totals || {}).filter(([, v]) => v.count > 0).sort((a, b) => b[1].seconds - a[1].seconds);
  const all = list.reduce((s, [, v]) => s + v.seconds, 0) || 1;
  if (!list.length) return <p className="text-sm text-slate-400" data-testid="uptime-no-outages">No outages with a recorded cause in this period.</p>;
  return (
    <div className="space-y-3" data-testid="uptime-cause-totals">
      <div className="flex h-3 rounded-full overflow-hidden bg-slate-100 dark:bg-slate-800">
        {list.map(([c, v]) => <div key={c} className={(CAUSE_META[c] || CAUSE_META.unknown).bar} style={{ width: `${(v.seconds / all) * 100}%` }} title={`${(CAUSE_META[c] || CAUSE_META.unknown).label}: ${humanSecs(v.seconds)}`} />)}
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {list.map(([c, v]) => (
          <div key={c} className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 dark:border-slate-800 px-3 py-2" data-testid={`uptime-cause-${c}`}>
            <CauseBadge cause={c} />
            <span className="text-xs text-slate-500 whitespace-nowrap"><b className="text-slate-900 dark:text-white">{v.count}</b> × · {humanSecs(v.seconds)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const causeText = (k) => Object.entries(k.causes).map(([c, v]) => `${(CAUSE_META[c] || CAUSE_META.unknown).label} ×${v.count} (${humanSecs(v.seconds)})`).join("; ");

export default function UptimeReport() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    let live = true;
    setData(null);
    setError("");
    api.get(`/reports/uptime?days=${days}`).then((r) => { if (live) setData(r.data); }).catch((e) => { if (live) setError(formatApiErrorDetail(e.response?.data?.detail)); });
    return () => { live = false; };
  }, [days]);

  const exportCsv = () => {
    const cell = (v) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const rows = [["Kiosk", "Code", "Building", "Uptime %", "Downtime", "Outages", "Longest", "Several-kiosk outages", "Causes", "Status"],
      ...data.kiosks.map((k) => [k.name, k.code, k.building, k.uptime_pct, humanSecs(k.downtime_seconds), k.outages, humanSecs(k.longest_seconds), k.fleet_wide, causeText(k), k.status])];
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([rows.map((r) => r.map(cell).join(",")).join("\n")], { type: "text/csv" }));
    a.download = `uptime-${days}d-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 min-w-0">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-admin-head text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white">Uptime &amp; Offline Causes</h1>
          <p className="text-slate-500 mt-1">How reliable each kiosk is, and why it went offline.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-slate-200 dark:border-slate-800 p-0.5" data-testid="uptime-period">
            {PERIODS.map((p) => (
              <button key={p} data-testid={`uptime-period-${p}`} onClick={() => setDays(p)} className={`px-3 py-1.5 text-sm font-semibold rounded-md transition-colors ${days === p ? "bg-blue-600 text-white" : "text-slate-500 hover:text-slate-900 dark:hover:text-white"}`}>{p}d</button>
            ))}
          </div>
          <Button variant="outline" size="sm" disabled={!data?.kiosks?.length} onClick={exportCsv} data-testid="uptime-csv"><Download className="h-4 w-4 mr-1.5" /> CSV</Button>
        </div>
      </div>

      {error ? <p className="text-rose-600 text-sm" data-testid="uptime-error">{error}</p> : !data ? <p className="text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /> Loading report…</p> : (
        <>
          <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-4 space-y-3">
            <p className="font-semibold text-slate-900 dark:text-white flex items-center gap-2"><Signal className="h-4 w-4 text-blue-600" /> Causes across all kiosks · last {days} days</p>
            <CauseTotals totals={data.totals} />
          </div>
          <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow><TableHead>Kiosk</TableHead><TableHead className="text-right">Uptime</TableHead><TableHead className="text-right">Downtime</TableHead><TableHead className="text-right">Outages</TableHead><TableHead className="text-right">Longest</TableHead><TableHead>Causes</TableHead></TableRow>
                </TableHeader>
                <TableBody>
                  {data.kiosks.map((k) => (
                    <TableRow key={k.kiosk_id} data-testid={`uptime-row-${k.code}`}>
                      <TableCell>
                        <p className="font-medium text-slate-900 dark:text-white">{k.name} {k.status === "offline" && <Badge variant="destructive" className="ml-1">Offline{k.ongoing_seconds ? ` ${humanSecs(k.ongoing_seconds)}` : ""}</Badge>}</p>
                        <p className="text-xs text-slate-400">{k.code} · {k.building || "—"}</p>
                      </TableCell>
                      <TableCell className={`text-right font-bold ${pctCls(k.uptime_pct)}`} data-testid={`uptime-pct-${k.code}`}>{k.uptime_pct.toFixed(2)}%</TableCell>
                      <TableCell className="text-right text-sm">{humanSecs(k.downtime_seconds)}</TableCell>
                      <TableCell className="text-right text-sm">{k.outages}{k.fleet_wide ? <span className="text-[10px] text-fuchsia-600 block">{k.fleet_wide} with other kiosks</span> : null}</TableCell>
                      <TableCell className="text-right text-sm">{humanSecs(k.longest_seconds)}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {Object.entries(k.causes).sort((a, b) => b[1].seconds - a[1].seconds).map(([c, v]) => (
                            <span key={c} className="inline-flex items-center gap-1" title={humanSecs(v.seconds)}><CauseBadge cause={c} /><span className="text-[11px] text-slate-500">×{v.count}</span></span>
                          ))}
                          {!Object.keys(k.causes).length && <span className="text-xs text-slate-400">—</span>}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!data.kiosks.length && <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-8">No kiosks have reported in yet.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </div>
          <p className="text-xs text-slate-400">Causes are worked out from each tablet's own diary when it reconnects. Outages from before this feature was switched on count towards downtime but have no recorded cause.</p>
        </>
      )}
    </div>
  );
}
